import axios from "axios";
import { getAuth } from "firebase/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: use localStorage authToken, or Firebase ID token when available
apiClient.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem("authToken");
    if (!token) {
      const user = getAuth().currentUser;
      if (user) token = await user.getIdToken();
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling (normalize so callers get message/error)
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const data = error.response?.data;
    const message = data?.message || data?.error || error.message;
    console.error(
      "API Error:",
      message || error.response?.data || error.message
    );
    const err = new Error(
      typeof message === "string" ? message : "Request failed"
    );
    err.response = error.response;
    err.data = data;
    return Promise.reject(err);
  }
);

// Stripe Connect API
export const connectApi = {
  // Initialize Connect account
  initLandlord: (landlordId) =>
    apiClient.post(`/connect/landlords/${landlordId}/init`),

  // Get account status
  getLandlordStatus: (landlordId) =>
    apiClient.get(`/connect/landlords/${landlordId}/status`),

  // Create onboarding link
  createOnboardingLink: (landlordId) =>
    apiClient.post(`/connect/landlords/${landlordId}/onboarding-link`),
};

// Plaid API
export const plaidApi = {
  // Create Plaid link token
  createLinkToken: (tenantId) =>
    apiClient.post(`/plaid/tenants/${tenantId}/plaid/link-token`),

  // Exchange public token
  exchangeToken: (tenantId, publicToken) =>
    apiClient.post(`/plaid/tenants/${tenantId}/plaid/exchange`, {
      publicToken,
    }),

  // List bank accounts
  getBankAccounts: (tenantId) =>
    apiClient.get(`/plaid/tenants/${tenantId}/bank-accounts`),

  // Process card payment
  processCardPayment: (invoiceId, paymentData) =>
    apiClient.post(`/plaid/${invoiceId}/pay`, paymentData),

  // Process ACH payment
  processAchPayment: (invoiceId, paymentData) =>
    apiClient.post(`/plaid/${invoiceId}/pay-ach`, paymentData),

  // Get payment details
  getPaymentDetails: (paymentId) => apiClient.get(`/plaid/${paymentId}`),

  // Process refund
  processRefund: (paymentId, refundData) =>
    apiClient.post(`/plaid/${paymentId}/refund`, refundData),

  // Create card setup intent
  createCardSetupIntent: (tenantId) =>
    apiClient.post(`/plaid/tenants/${tenantId}/cards/setup-intent`),
};

// Invoice API
export const invoiceApi = {
  // Generate monthly invoices
  generateMonthlyInvoices: (data) => apiClient.post("/invoices/", data),

  // Generate lease invoice
  generateLeaseInvoice: (data) => apiClient.post("/invoices/lease", data),

  // Get invoice details
  getInvoiceDetails: (invoiceId) => apiClient.get(`/invoices/${invoiceId}`),

  // Get tenant invoices
  getTenantInvoices: (tenantId) =>
    apiClient.get(`/invoices/tenant/${tenantId}`),

  // Get landlord invoices
  getLandlordInvoices: (landlordId) =>
    apiClient.get(`/invoices/landlord/${landlordId}`),

  // Apply late fees
  applyLateFees: (data) => apiClient.post("/invoices/apply-late-fees", data),

  // Add adjustment/credit to invoice
  addAdjustment: (invoiceId, adjustmentData) =>
    apiClient.post(`/invoices/${invoiceId}/adjustments`, adjustmentData),

  // Remove adjustment from invoice
  removeAdjustment: (invoiceId, adjustmentId) =>
    apiClient.delete(`/invoices/${invoiceId}/adjustments/${adjustmentId}`),
};

// Payment status constants
export const PAYMENT_STATUS = {
  PENDING: "pending",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  REFUNDED: "refunded",
  CANCELLED: "cancelled",
};

// Payment method constants
export const PAYMENT_METHOD = {
  CARD: "card",
  ACH: "ach",
};

// Stripe API endpoints; backend router is mounted at /api/frontend-stripe
export const stripeApi = {
  // Create payment intent
  createPaymentIntent: (data) =>
    apiClient.post("/frontend-stripe/create-payment-intent", data),

  // Confirm payment (deprecated on backend; use client-side Stripe.js instead)
  confirmPayment: (data) => apiClient.post("/frontend-stripe/confirm-payment", data),

  // Save payment method
  savePaymentMethod: (data) =>
    apiClient.post("/frontend-stripe/save-payment-method", data),

  // Get a single payment method by ID
  getPaymentMethod: (paymentMethodId) =>
    apiClient.get(`/frontend-stripe/payment-method/${paymentMethodId}`),

  // Stripe Connect - Create account
  createConnectAccount: (data) =>
    apiClient.post("/frontend-stripe/connect/create-account", data),

  // Stripe Connect - Create account link
  createAccountLink: (data) =>
    apiClient.post("/frontend-stripe/connect/create-account-link", data),

  // Stripe Connect - Get account status
  getConnectAccountStatus: (accountId) =>
    apiClient.get(`/frontend-stripe/connect/account-status/${accountId}`),

  // Stripe Connect - Get account details (no dedicated backend route; use account-status)
  getConnectAccountDetails: (accountId) =>
    apiClient.get(`/frontend-stripe/connect/account-status/${accountId}`),

  // Stripe Connect - Create login link
  // If the account hasn't completed onboarding the backend automatically returns an onboarding link.
  // @param {string} accountId - Stripe Connect account ID
  // @param {string} [returnUrl] - URL to redirect to after onboarding/login completes
  // @param {string} [refreshUrl] - URL to redirect to if user needs to refresh/retry
  createLoginLink: (accountId, returnUrl, refreshUrl) =>
    apiClient.post(`/frontend-stripe/connect/create-login-link`, {
      accountId,
      returnUrl,
      refreshUrl,
    }),

  // Webhook handler; stays on /stripe/webhook (raw body, separate router)
  handleWebhook: (data) => apiClient.post("/stripe/webhook", data),
};

export default {
  connect: connectApi,
  plaid: plaidApi,
  invoice: invoiceApi,
  stripe: stripeApi,
};
